/**
 * Adapts adults/children capacity limits to the selected rental unit's capacity.
 * Supports standard HTML <select> elements and <input type="number"> steppers.
 * STRICT ZERO SHOELACE components.
 */
export function initCapacityAdaptation(formElement) {
  if (!formElement) return;

  const bcEl = document.getElementById("booking-calculation");
  const datasetUnits =
    formElement.dataset.rentalUnits ||
    bcEl?.dataset.units ||
    bcEl?.dataset.rental;
  if (!datasetUnits) return;

  let rentalData;
  try {
    rentalData = JSON.parse(datasetUnits);
  } catch (e) {
    return;
  }

  const rentalUnits = Array.isArray(rentalData)
    ? rentalData
    : rentalData.rental_units || rentalData.units || [];

  if (!Array.isArray(rentalUnits) || rentalUnits.length === 0) return;

  const adultsInput =
    formElement.querySelector("#adults") ||
    formElement.querySelector("#booking_adults");
  const childrenInput =
    formElement.querySelector("#children") ||
    formElement.querySelector("#booking_children");
  const dogsInput =
    formElement.querySelector("#dogs") ||
    formElement.querySelector("#booking_dogs");
  const rentalUnitSelect =
    formElement.querySelector("#rentalUnitId") ||
    formElement.querySelector("#rental_unit_id") ||
    formElement.querySelector('[name="rental_unit_id"]') ||
    formElement.querySelector("#booking_rental_unit_id") ||
    formElement.querySelector("#booking_rentalUnitId") ||
    formElement.querySelector('[name="booking[rentalUnitId]"]');

  if (!adultsInput || !childrenInput) return;

  const findUnit = (value) =>
    rentalUnits.find((u) => String(u.id) === String(value));

  let selectedUnit = rentalUnitSelect
    ? findUnit(rentalUnitSelect.value) || rentalUnits[0]
    : rentalUnits[0];
  let maxCapacity = (selectedUnit.capacity || 1) * (selectedUnit.count || 1);

  const updateNumericInput = (input, max) => {
    input.setAttribute("max", String(max));
    const val = parseInt(input.value, 10) || 0;
    if (val > max) {
      input.value = String(max);
      input.dispatchEvent(new Event("change", { bubbles: true }));
      input.dispatchEvent(new Event("input", { bubbles: true }));
    }
  };

  const updateSelectOptions = (select, newMax, selectedValue, category) => {
    const currentVal =
      parseInt(selectedValue, 10) || (category === "adults" ? 1 : 0);
    const minVal = category === "adults" ? 1 : 0;
    select.replaceChildren();

    for (let i = minVal; i <= newMax; i++) {
      const opt = document.createElement("option");
      opt.value = String(i);
      opt.textContent = global.Translator?.transChoice
        ? global.Translator.transChoice(
            `booking.fields.${category}.values`,
            i,
            { count: i },
            "js"
          )
        : `${i}`;
      select.appendChild(opt);
    }

    const clamped = Math.min(Math.max(currentVal, minVal), newMax);
    select.value = String(clamped);
  };

  const adaptField = (input, max, category) => {
    if (input.tagName.toLowerCase() === "select") {
      updateSelectOptions(input, max, input.value, category);
    } else {
      updateNumericInput(input, max);
    }
  };

  const updateExplanatoryLabels = () => {
    const hintEl =
      document.getElementById("booking-capacity-hint") ||
      formElement.querySelector(".js-unit-capacity-hint");
    const uCount = selectedUnit.count || 1;
    const uCap = selectedUnit.capacity || 1;
    const total = uCount * uCap;

    if (hintEl) {
      if (uCount > 1) {
        hintEl.textContent = global.Translator?.trans
          ? global.Translator.trans(
              "units.capacity_multi_hint",
              { count: uCount, capacity: uCap, total: total },
              "js"
            )
          : `Bis zu ${total} Gäste möglich (${uCount} Unterkünfte à ${uCap} Personen)`;
        hintEl.classList.remove("hidden");
      } else {
        hintEl.textContent = "";
        hintEl.classList.add("hidden");
      }
    }

    const adultsCapacityLabel = document.getElementById("adults-capacity-label");
    if (adultsCapacityLabel) {
      const suffix =
        adultsCapacityLabel.dataset.capacitySuffix ||
        (global.Translator?.trans
          ? global.Translator.trans("units.capacity", {}, "messages")
          : "Max. Gäste");
      if (uCount > 1) {
        adultsCapacityLabel.textContent = `${total} ${suffix} (${uCount}x ${uCap})`;
      } else {
        adultsCapacityLabel.textContent = `${uCap} ${suffix}`;
      }
    }
  };

  const adapt = () => {
    const currentAdults = parseInt(adultsInput.value, 10) || 1;
    adaptField(adultsInput, maxCapacity, "adults");
    adaptField(
      childrenInput,
      Math.max(0, maxCapacity - currentAdults),
      "children"
    );

    if (dogsInput) {
      const isNullDogCount =
        selectedUnit.dog_count === null ||
        selectedUnit.dog_count === undefined ||
        selectedUnit.dog_count === "";
      const isPnUnlimited =
        (selectedUnit.pets_allowed || selectedUnit.pnPetsAllowed) &&
        isNullDogCount;
      const maxDogs = !isNullDogCount
        ? typeof selectedUnit.dog_count === "number"
          ? selectedUnit.dog_count
          : parseInt(selectedUnit.dog_count, 10)
        : 5;

      if (dogsInput.tagName && dogsInput.tagName.toLowerCase() !== "select") {
        if (isNullDogCount) {
          dogsInput.removeAttribute("max");
        } else {
          updateNumericInput(dogsInput, maxDogs);
        }
        const dogsDisabled = maxDogs === 0 && !isPnUnlimited;
        dogsInput.toggleAttribute("readonly", dogsDisabled);
        dogsInput
          .closest(".quantitystepper")
          ?.querySelectorAll("button")
          ?.forEach((btn) => {
            btn.disabled = dogsDisabled;
          });
      } else {
        updateSelectOptions(dogsInput, maxDogs, dogsInput.value, "pets");
      }
    }

    const globalPeriodMin =
      parseInt(formElement.dataset.periodMin || "0", 10) || 0;
    const minPeriod = Math.max(selectedUnit.period_min || 0, globalPeriodMin);
    const periodMinEl = document.getElementById("booking-period-min");
    if (periodMinEl) {
      periodMinEl.textContent =
        minPeriod > 1 && global.Translator?.transChoice
          ? global.Translator.transChoice(
              "units.period_min",
              minPeriod,
              { count: minPeriod },
              "js"
            )
          : "";
    }

    updateExplanatoryLabels();
  };

  if (rentalUnitSelect) {
    rentalUnitSelect.addEventListener("change", () => {
      const next = findUnit(rentalUnitSelect.value);
      if (!next) return;
      selectedUnit = next;
      maxCapacity = (selectedUnit.capacity || 1) * (selectedUnit.count || 1);
      adapt();
    });
  }

  const onAdultsUpdate = () => {
    const currentAdults = parseInt(adultsInput.value, 10) || 1;
    adaptField(
      childrenInput,
      Math.max(0, maxCapacity - currentAdults),
      "children"
    );
  };

  adultsInput.addEventListener("change", onAdultsUpdate);
  adultsInput.addEventListener("input", onAdultsUpdate);

  adapt();
}

export default initCapacityAdaptation;
